'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { Tag } from '@eversports/klimt-primitives/Tag'

import getDifferenceInMinutes from '../../../../../../utils/get-difference-in-minutes'
import formatLocation from '../../../../../../lib/format-location'
import { Routes } from '../../../../../../App.constants'
import SeoLink from '../../../../../../../components/seoLink'
import { PLACEHOLDER_IMAGE } from '../../../../../../App.images'
import StarRating from '../../../../../../components/StarRating'
import type { Locale } from '../../../../../../../localization/i18n'

import { EventBookableItem } from './ActivityCard.types'
import { LEVEL_TRANSLATION_MAPPING } from './ActivityCard.constants'
import RegistrationStateBadges from './components/RegistrationStateBadges'
import formatDate from './helpers/format-date'
import formatTime from './helpers/format-time'

interface Props {
  activity: EventBookableItem
  locale: Locale
  hasPriority?: boolean
}

const ActivityCard = ({ activity, locale, hasPriority = false }: Props) => {
  const translate = useTranslations('discover-sport-city')

  if (!activity.eventSessions.length) {
    return null
  }

  const {
    venue: { id: venueId, name: venueName, rating, reviewCount },
    sport,
    level,
    registrationState,
    location,
    eventSessions,
  } = activity

  const { id, name, start, end, image, hasOnlineStream, availableSpots } = eventSessions[0]

  // TODO - Was getting this from the venue, but some venue's don't have timezones.
  // This caused a backend error, and the page to crash
  // Core team is fixing this - so after that remove this line and get again from the query
  const timeZone = 'Europe/Vienna'

  const date = formatDate({ dateStr: start, locale, timeZone })
  const startTime = formatTime({ dateStr: start, locale, timeZone })
  const endTime = formatTime({ dateStr: end, locale, timeZone })
  const duration = getDifferenceInMinutes(start, end)
  const address = location ? formatLocation(location) : undefined
  const isOutdoor = !hasOnlineStream && location && !location.indoor

  const levelTranslationKey = level && LEVEL_TRANSLATION_MAPPING[level]

  return (
    <li className="relative overflow-hidden border border-border-divider bg-white shadow-sm focus-within:ring hover:shadow-md sm:rounded-md">
      <SeoLink
        className="flex h-full flex-col justify-between px-2 py-3"
        type="hidden"
        encodedTo={btoa(`${Routes.ACTIVITY_DETAILS}/${id}`)}
      >
        <div className="flex flex-col">
          <h2 className="truncate font-bold">{name}</h2>
          <span className="mb-2 text-base">
            {translate('activity-date-time', { date, startTime, endTime, duration })}
          </span>
          <div className="mb-2 flex gap-1">
            <Image
              src={image?.small || PLACEHOLDER_IMAGE}
              alt="" // using the activity name was marked as redundant
              loading={hasPriority ? 'eager' : 'lazy'}
              priority={hasPriority}
              width={100}
              height={68}
              className="h-[68px] w-[100px] shrink-0 rounded-md object-cover"
            />
            <div className="flex flex-col gap-0.5 overflow-hidden border-l border-border-divider pl-1 text-base">
              <span className="truncate">{venueName}</span>
              <div className="flex items-center gap-0.5 text-sm text-text-secondary">
                <StarRating rating={rating} venueId={venueId} size="small" />
                {Number(rating).toFixed(1)} ({reviewCount})
              </div>
              {hasOnlineStream ? translate('online') : address && <span>{address}</span>}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1 capitalize">
          <Tag variant="custom" style="outline" text={sport.name} />
          {levelTranslationKey && <Tag variant="custom" style="outline" text={translate(levelTranslationKey)} />}
          {isOutdoor && <Tag variant="custom" style="outline" text={translate('offering-outdoor')} />}
          <RegistrationStateBadges availableSpots={availableSpots} registrationState={registrationState} />
        </div>
      </SeoLink>
    </li>
  )
}

export default ActivityCard
